import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Mail, 
  Phone, 
  Linkedin, 
  Copy, 
  Check, 
  Send, 
  FileDown, 
  Sparkles, 
  MapPin,
  Clock,
  ArrowUpRight,
  ArrowRight,
  Loader2
} from "lucide-react";

export default function ContactSection() {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    needs: "Full SEO & AI Search Strategy",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const email = "narendrakumarkanna@gmail.com";
  const phone = "+91 8464094525";
  const linkedin = "https://www.linkedin.com/in/narendra-kumar-kanna/";

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      createdAt: new Date().toISOString()
    };

    try {
      // 1. Post to API backend
      const res = await fetch("/api/audit-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Server returned error status ${res.status}`);
      }

      // 2. Also save to localStorage as a client-side safety backup
      try {
        const existing = JSON.parse(localStorage.getItem("narendra_audit_requests_backup") || "[]");
        existing.unshift({ ...payload, id: `local_${Date.now()}` });
        localStorage.setItem("narendra_audit_requests_backup", JSON.stringify(existing));
      } catch (err) {
        console.warn("Storage backup error:", err);
      }

      setSubmittedData(payload);
      setIsSubmitted(true);
    } catch (err) {
      console.warn("Network issue during form submission, using offline cache:", err);
      // Still store in localStorage backup
      try {
        const existing = JSON.parse(localStorage.getItem("narendra_audit_requests_backup") || "[]");
        existing.unshift({ ...payload, id: `offline_${Date.now()}` });
        localStorage.setItem("narendra_audit_requests_backup", JSON.stringify(existing));
      } catch (localErr) {
        console.warn("Storage backup error:", localErr);
      }
      setSubmittedData(payload);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-11 lg:py-14 bg-[#080d1a] relative overflow-hidden">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-indigo-600/15 rounded-full blur-[160px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Heading */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-7 sm:mb-8"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2.5 font-semibold">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            Let's Collaborate
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-[2.5rem] text-white tracking-tight uppercase mb-2 leading-tight">
            Accelerate Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400">Search Pipeline</span>
          </h2>
          <p className="text-sm sm:text-base text-white/70 font-medium leading-relaxed max-w-2xl mx-auto">
            Whether you are planning an enterprise website migration, seeking to double organic SQLs, or looking to optimize for AI answer engines (AEO/GEO), let's discuss your roadmap.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          
          {/* Left Column: Direct Contact Info & Quick Actions */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5 flex flex-col justify-between"
          >
            
            {/* Quick Contact Card */}
            <div className="p-6 sm:p-7 rounded-3xl bg-[#0c1222] border border-white/10 shadow-2xl h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between pb-2.5 border-b border-white/[0.08] mb-4">
                  <span className="text-xs font-mono uppercase tracking-wider text-white/50 font-semibold">
                    Direct Channels
                  </span>
                  <span className="text-xs font-mono text-emerald-400 font-bold">Fast Response</span>
                </div>

                <div className="space-y-3">
                  {/* Email Item */}
                  <div className="p-3.5 sm:p-4 rounded-2xl bg-[#080d1a] border border-white/[0.06] flex items-center justify-between group">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <p className="text-[10px] font-mono text-white/40 uppercase">Email</p>
                        <a 
                          href={`mailto:${email}`}
                          className="text-xs sm:text-sm font-semibold text-white hover:text-indigo-400 transition-colors truncate block"
                        >
                          {email}
                        </a>
                      </div>
                    </div>

                    <button
                      onClick={handleCopyEmail}
                      className="p-2.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors flex-shrink-0 ml-2"
                      title="Copy Email Address"
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Phone Item */}
                  <div className="p-3.5 sm:p-4 rounded-2xl bg-[#0b0b0f] border border-white/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-white/40 uppercase">Phone / WhatsApp</p>
                        <a 
                          href="tel:8464094525" 
                          className="text-xs sm:text-sm font-semibold text-white hover:text-blue-400 transition-colors"
                        >
                          {phone}
                        </a>
                      </div>
                    </div>
                    <a
                      href="https://wa.me/918464094525"
                      target="_blank"
                      rel="noreferrer"
                      className="p-2.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>

                  {/* LinkedIn Item */}
                  <div className="p-3.5 sm:p-4 rounded-2xl bg-[#0b0b0f] border border-white/[0.06] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-400 flex items-center justify-center flex-shrink-0">
                        <Linkedin className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-white/40 uppercase">Professional Network</p>
                        <a 
                          href={linkedin}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs sm:text-sm font-semibold text-white hover:text-sky-400 transition-colors"
                        >
                          linkedin.com/in/narendra-kumar-kanna
                        </a>
                      </div>
                    </div>
                    <a
                      href={linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Status & Location Notes */}
                <div className="mt-4 pt-3.5 border-t border-white/[0.08] space-y-2">
                  <div className="flex items-center gap-2 text-xs text-white/70">
                    <MapPin className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <span>Available for Global Remote, Hybrid, & Advisory Engagements</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/70">
                    <Clock className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Prompt Response Within 24 Business Hours</span>
                  </div>
                </div>
              </div>

              {/* Resume Action */}
              <div className="mt-4 pt-3.5 border-t border-white/[0.08]">
                <a
                  href="/Narendra-Resume-2026.pdf"
                  download="Kanna_Narendra_Kumar_Resume.pdf"
                  className="w-full py-3 px-4 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.12] text-white/90 hover:text-white font-semibold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                >
                  <FileDown className="w-4 h-4 text-cyan-400" />
                  <span>Download Complete Resume (PDF)</span>
                </a>
              </div>
            </div>

          </motion.div>

          {/* Right Column: Interactive Consultation Inquiry Form */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="p-6 sm:p-7 rounded-3xl bg-[#0c1222] border border-white/10 shadow-2xl relative h-full flex flex-col justify-between">
              {isSubmitted ? (
                <div className="text-center py-10 px-4 space-y-5">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
                    <Check className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
                      Inquiry & Audit Request Received!
                    </h3>
                    <p className="text-sm text-white/70 max-w-md mx-auto leading-relaxed mt-2">
                      Thank you, <span className="text-cyan-400 font-semibold">{submittedData?.name || "there"}</span>. Your inquiry details have been saved and routed directly to Narendra's inbox (<span className="text-white font-mono text-xs">narendrakumarkanna@gmail.com</span>).
                    </p>
                  </div>

                  {submittedData && (
                    <div className="p-4 rounded-2xl bg-[#080d19] border border-white/[0.08] max-w-md mx-auto text-left space-y-1.5 text-xs">
                      <div className="flex justify-between text-white/50 font-mono">
                        <span>Domain / Company:</span>
                        <span className="text-white font-medium">{submittedData.company || "Not specified"}</span>
                      </div>
                      <div className="flex justify-between text-white/50 font-mono">
                        <span>Selected Need:</span>
                        <span className="text-cyan-300 font-medium">{submittedData.needs}</span>
                      </div>
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      onClick={() => {
                        setFormData({
                          name: "",
                          email: "",
                          company: "",
                          needs: "Full SEO & AI Search Strategy",
                          message: ""
                        });
                        setIsSubmitted(false);
                      }}
                      className="px-6 py-2.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.15] border border-white/10 text-white text-xs font-mono uppercase tracking-wider transition-colors cursor-pointer"
                    >
                      Send Another Request
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 flex flex-col justify-between h-full">
                  {/* Anti-spam Bot Trap Honeypot (Completely hidden from real users) */}
                  <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
                    <input
                      type="text"
                      name="website_url_hp"
                      tabIndex="-1"
                      autoComplete="off"
                      value={formData.website_url_hp || ""}
                      onChange={(e) => setFormData(prev => ({ ...prev, website_url_hp: e.target.value }))}
                    />
                  </div>

                  <div className="flex items-center justify-between pb-2.5 border-b border-white/[0.08]">
                    <span className="text-xs font-mono uppercase tracking-wider text-white/60 font-semibold">
                      Send a Direct Message
                    </span>
                    <span className="text-xs font-mono text-cyan-400 font-bold">Confidential & Direct</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-white/50 mb-1.5">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Sarah Jenkins"
                        className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-white/50 mb-1.5">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="sarah@gmail.com"
                        className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-white/50 mb-1.5">
                        Company / Website Domain
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="e.g. yourcompany.com"
                        className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-white/50 mb-1.5">
                        Primary Need
                      </label>
                      <select
                        value={formData.needs}
                        onChange={(e) => setFormData({ ...formData, needs: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                      >
                        <option value="Enterprise CMS Migration">Enterprise CMS Migration (WP to Sitecore)</option>
                        <option value="AEO & Generative AI Search">AI Search Visibility (AEO / GEO / HEO)</option>
                        <option value="Content Strategy with High Intent">Content Strategy with High Intent</option>
                        <option value="Full Technical Audit">Comprehensive Technical SEO Audit</option>
                        <option value="Full-Time Role Offer">Full-Time / Senior Role Discussion</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-white/50 mb-1.5">
                      Your Message or Project Scope *
                    </label>
                    <textarea
                      required
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Share brief details about your website, traffic goals, or role requirements..."
                      className="w-full px-4 py-2.5 rounded-xl bg-[#080d19] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 rounded-xl btn-bold-primary text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed shadow-lg hover:shadow-cyan-500/20"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Sending Request & Notifying Narendra...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Message to Narendra</span>
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    <p className="text-center text-[11px] text-white/40 mt-2">
                      Your details are never shared. Direct inquiries reach Narendra's personal inbox.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
